import React, { useState, useEffect, useRef } from "react"; // Add useRef import
import WireBindingOptions from "./WireBindingOptions";
import PaperSelector from "./PaperSelector"; // Import the PaperSelector component
import FinishingMaterialOptions from "./FinishingMaterialOptions"; // Updated import
import { isFinishAvailable } from "./finishingAvailability";

const BookletOptionsForm = ({
  jobType,
  bindingType, // Keep receiving bindingType
  handleBindingTypeChange, // NEW: Receive the new handler
  pageCount,
  setPageCount,
  separateCover,
  setSeparateCover,
  coverStock,
  setCoverStock,
  paperStocks,
  selectedFinishes,
  finishingMaterials,
  finishingOptions,
  handleOptionChange,
  handleCheckboxChange,
  bookletDetails,
  coverColours,
  setCoverColours,
  finishingProcesses,
  // Cover Finishing Props
  selectedCoverFinishes,
  toggleCoverFinishingProcess,
  coverFinishingOptions,
  handleCoverOptionChange,
  handleCoverCheckboxChange,
  // Cover Sides Props
  coverIsDoubleSided,
  setCoverIsDoubleSided,
  // User modification tracking
  markFieldAsUserModified,
}) => {
  // New state for wire-bound cover configuration
  // 'none': no separate cover
  // 'front_only': separate front cover
  // 'front_and_back': separate front and back covers (same stock)
  const [wireCoverConfig, setWireCoverConfig] = useState("none");

  // Ref to track if we've set the initial default for the current binding type
  const hasSetInitialDefaultRef = useRef(false);

  // Effect to restore from finishingOptions when they're loaded (only run once)
  useEffect(() => {
    if (
      bindingType === "wire_bound" &&
      finishingOptions?.wirebind?.coverType &&
      !hasSetInitialDefaultRef.current
    ) {
      const savedCoverType = finishingOptions.wirebind.coverType;
      if (["none", "front_only", "front_and_back"].includes(savedCoverType)) {
        setWireCoverConfig(savedCoverType);
        hasSetInitialDefaultRef.current = true;
      }
    }
  }, [bindingType, finishingOptions?.wirebind?.coverType]); // More specific dependency

  // Effect to handle wirebound initialization (only run once)
  useEffect(() => {
    if (bindingType === "wire_bound" && !hasSetInitialDefaultRef.current) {
      // Only set default if there's no saved data
      if (!finishingOptions?.wirebind?.coverType) {
        setWireCoverConfig("front_only");
        hasSetInitialDefaultRef.current = true;
      }
    } else if (bindingType !== "wire_bound") {
      // Reset when not wirebound
      setWireCoverConfig("none");
      hasSetInitialDefaultRef.current = false;
    }
  }, [bindingType]); // Only depend on bindingType change

  // Separate effect for wireCoverConfig changes (user interactions)
  useEffect(() => {
    if (bindingType === "wire_bound" && hasSetInitialDefaultRef.current) {
      const newSeparateCover = wireCoverConfig !== "none";
      if (separateCover !== newSeparateCover) {
        setSeparateCover(newSeparateCover);
      }

      const currentCoverType = finishingOptions?.wirebind?.coverType;
      if (currentCoverType !== wireCoverConfig) {
        handleOptionChange("wirebind", "coverType", wireCoverConfig);
      }
    }
  }, [wireCoverConfig]); // Only trigger on wireCoverConfig changes

  // Filter out wirebind process from general finishing options if it's handled separately
  const selectedCoverStock = paperStocks.find((stock) => stock.id === coverStock);
  const filteredFinishingProcesses = finishingProcesses.filter(
    (proc) =>
      ![
        "wirebind",
        "perfect-bind",
        "saddle-stitch",
        "perforate",
        "collate",
        "cut",
      ].includes(proc.id) &&
      isFinishAvailable(proc, selectedCoverStock, jobType),
  );

  return (
    <div className="md:col-span-2 border p-4 rounded bg-gray-50">
      <h3 className="font-medium mb-3">Booklet Options</h3>

      {/* BINDING TYPE SELECTOR */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Binding Type</label>
        <select
          value={bindingType}
          onChange={(e) => handleBindingTypeChange(e.target.value)} // Use the new handler
          className="w-full p-2 border rounded"
        >
          <option value="saddle-stitch">Saddle Stitch</option>
          <option value="wire_bound">Wire Bound</option>
          <option value="perfect_bound">Perfect Bound</option>
        </select>
      </div>

      {/* Page Count Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Number of Pages
        </label>
        <input
          type="number"
          value={pageCount === 0 ? "" : pageCount}
          onChange={(e) => {
            const rawValue = e.target.value;
            const newValue = rawValue === "" ? 0 : Number(rawValue);
            setPageCount(newValue);
          }}
          onBlur={() => {
            if (bindingType === "saddle-stitch") {
              let finalPageCount = 4;
              if (typeof pageCount === "number" && pageCount >= 4) {
                finalPageCount = Math.ceil(pageCount / 4) * 4;
              }
              if (pageCount !== finalPageCount) {
                setPageCount(finalPageCount);
              }
            } else {
              if (typeof pageCount !== "number" || pageCount <= 0) {
                setPageCount(2); // Minimum for other binding types
              }
            }
          }}
          className="w-full p-2 border rounded"
          step={bindingType === "saddle-stitch" ? "4" : "1"}
          min={bindingType === "saddle-stitch" ? "4" : "2"}
          placeholder="e.g., 16"
        />
        <p className="text-xs text-gray-500 mt-1">
          {bindingType === "saddle-stitch"
            ? "Must be a multiple of 4 for Saddle Stitch"
            : "Enter total number of pages - (total sides, not leaves) - including the cover"}
        </p>
      </div>

      {/* Conditional Cover Selection */}
      {bindingType === "wire_bound" ? (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Cover Configuration
          </label>
          <select
            value={wireCoverConfig}
            onChange={(e) => {
              setWireCoverConfig(e.target.value);
              // Mark that user has made an explicit choice
              hasSetInitialDefaultRef.current = true;
              // Track this as a user modification to prevent suggestion overrides
              if (markFieldAsUserModified) {
                markFieldAsUserModified("separateCover");
                // Don't track finishingOptions to avoid triggering suggestion reloads
              }
            }}
            className="w-full p-2 border rounded"
          >
            <option value="none">No Separate Cover</option>
            <option value="front_only">Separate Front Cover Only</option>
            <option value="front_and_back">Separate Front & Back Covers</option>
          </select>
        </div>
      ) : (
        <div className="mb-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="separateCover"
              checked={separateCover}
              onChange={(e) => {
                setSeparateCover(e.target.checked);
                // Track this as a user modification to prevent suggestion overrides
                if (markFieldAsUserModified) {
                  markFieldAsUserModified("separateCover");
                }
              }}
              className="mr-2"
            />
            <label htmlFor="separateCover" className="font-medium">
              Use separate stock for cover
            </label>
          </div>
        </div>
      )}
      {/* Cover Setup Options - Grouped with Visual Border */}
      {separateCover && (
        <div className="mb-6 p-4 border-4 border-blue-300 rounded-lg bg-blue-50/30">
          {/* Alternative yellow border option: */}
          {/* <div className="mb-6 p-4 border-4 rounded-lg bg-blue-50/30" style={{borderColor: '#FAFA00'}}> */}
          <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
            <span className="inline-block w-3 h-3 bg-blue-600 rounded-full mr-2"></span>
            {/* Alternative yellow bullet: */}
            {/* <span className="inline-block w-3 h-3 rounded-full mr-2" style={{backgroundColor: '#FAFA00'}}></span> */}
            Cover Setup Options
          </h4>

          {/* Cover Stock Selector - NOW USING PAPER SELECTOR */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Cover Stock
            </label>
            <PaperSelector
              paperStocks={paperStocks}
              selectedPaperId={coverStock}
              onChange={setCoverStock}
              className="w-full"
            />
          </div>

          {/* Cover Sides and Colour Selectors */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cover Sides */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Cover Sides
              </label>
              <div className="flex">
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-l-lg text-sm ${
                    !coverIsDoubleSided
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                  onClick={() => setCoverIsDoubleSided(false)}
                >
                  Single-sided
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-r-lg text-sm ${
                    coverIsDoubleSided
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                  onClick={() => setCoverIsDoubleSided(true)}
                >
                  Double-sided
                </button>
              </div>
            </div>
            {/* Cover Colour */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Cover Print Colour
              </label>
              <div className="flex">
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-l-lg text-sm ${
                    coverColours === "black_white"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                  onClick={() => setCoverColours("black_white")}
                >
                  Black & White
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-r-lg text-sm ${
                    coverColours === "full_colour"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                  onClick={() => setCoverColours("full_colour")}
                >
                  Full Colour
                </button>
              </div>
            </div>
          </div>
          <div className="mb-4">
            <p className="text-xs text-gray-500">
              Cover settings apply only to the cover pages. Inner pages will use
              the main sides and colour selections.
            </p>
          </div>

          {/* Cover Finishing Options */}
          {finishingProcesses && finishingProcesses.length > 0 && (
            <div className="mb-4 pt-4 border-t mt-4">
              <label className="block text-sm font-medium mb-2">
                Cover Finishing Options
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {filteredFinishingProcesses.map((process) => (
                  <div
                    key={`cover-finish-${process.id}`}
                    className="flex items-center"
                  >
                    <input
                      type="checkbox"
                      id={`cover-process-${process.id}`}
                      checked={selectedCoverFinishes.includes(process.id)}
                      onChange={() => toggleCoverFinishingProcess(process.id)}
                      className="mr-2"
                    />
                    <label
                      htmlFor={`cover-process-${process.id}`}
                      className="text-sm"
                    >
                      {process.name}
                    </label>

                    {/* Use the new FinishingMaterialOptions component for cover finishes */}
                    {selectedCoverFinishes.includes(process.id) &&
                      ["laminate", "duplex-laminate", "encapsulate"].includes(
                        process.id,
                      ) && (
                        <FinishingMaterialOptions
                          processId={process.id}
                          finishingOptions={coverFinishingOptions}
                          handleOptionChange={handleCoverOptionChange}
                          finishingMaterials={finishingMaterials}
                          className="ml-2"
                        />
                      )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select finishes to apply ONLY to the cover.
              </p>
            </div>
          )}
        </div>
      )}

      {/* --- Render Wire Binding Options (Conditional) --- */}
      {selectedFinishes.includes("wirebind") && (
        <WireBindingOptions
          finishingMaterials={finishingMaterials}
          finishingOptions={finishingOptions}
          handleOptionChange={handleOptionChange}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}

      {/* Booklet Details (Conditional) */}
      {bookletDetails && (
        <div className="mt-4 p-3 bg-blue-50 rounded text-sm">
          <p>
            <strong>Booklet details:</strong>
          </p>
          <p>
            Pages: {bookletDetails.original_page_count} (padded to{" "}
            {bookletDetails.padded_page_count})
          </p>
          <p>Sheets needed: {bookletDetails.sheets_needed}</p>
          <p>Pages per sheet: {bookletDetails.pages_per_sheet}</p>
          {bookletDetails.empty_pages > 0 && (
            <p className="text-blue-600">
              Note: {bookletDetails.empty_pages} blank pages will be added to
              make a complete booklet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BookletOptionsForm;
